<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useOrgStore } from '../stores/orgStore.js'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const orgStore = useOrgStore()

const REASON_MESSAGES = {
  org_deleted: 'The selected organization is deactivated. Choose another active organization.',
  suborg_deleted: 'The selected sub-organization is deactivated. Choose another active sub-organization.',
  lifecycle_context_invalid: 'Your organization context changed. Choose another active organization.',
}

const activeOrgIdModel = computed({
  get: () => (orgStore.activeOrgId === null ? '' : String(orgStore.activeOrgId)),
  set: (value) => {
    if (!value) {
      orgStore.setActiveOrg(null)
      return
    }

    orgStore.setActiveOrg(Number(value))
  },
})

const hasOrganizations = computed(() => orgStore.availableOrganizations.length > 0)
const reasonMessage = computed(() => {
  const reason = String(route.query?.reason ?? '')
  return REASON_MESSAGES[reason] ?? ''
})

const logout = async () => {
  authStore.logout()
  await router.push('/login')
}
</script>

<template>
  <section class="no-access-card">
    <h1>No access configured for this organization</h1>
    <p>Your account has not been assigned a role in the selected organization.</p>
    <p v-if="reasonMessage" class="reason-message">{{ reasonMessage }}</p>

    <div v-if="hasOrganizations" class="actions">
      <label for="no-access-org-select">Try another organization</label>
      <select id="no-access-org-select" v-model="activeOrgIdModel" :disabled="orgStore.loading">
        <option value="">Select organization</option>
        <option v-for="org in orgStore.availableOrganizations" :key="org.id" :value="String(org.id)">
          {{ org.name }}
        </option>
      </select>
    </div>

    <button class="logout" @click="logout">Logout</button>
  </section>
</template>

<style scoped>
.no-access-card {
  margin: 2rem auto;
  max-width: 640px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.25rem;
}

h1 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
}

p {
  margin: 0 0 1rem;
  color: #4b5563;
}

.reason-message {
  border-left: 4px solid #f59e0b;
  background: #fffbeb;
  color: #92400e;
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;
}

label {
  font-size: 0.9rem;
  color: #374151;
}

select {
  max-width: 320px;
  padding: 0.4rem 0.45rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.logout {
  border: none;
  border-radius: 6px;
  background: #dc2626;
  color: #fff;
  padding: 0.45rem 0.8rem;
}
</style>

